import { observer } from 'mobx-react-lite'
import React from 'react'
import { css, styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { IconButton } from '../../core/Components'
import * as Icons from '../../core/icons'
import withTooltip from '../../core/withTooltip'
import { gray15 } from '../../platform/color'

const StyledContainer = styled('div', {
  display: 'inline-flex',
  alignItems: 'center'
})

const StyledSlider = styled('input', {
  appearance: 'none',
  width: '100%',
  height: '2px',
  borderRadius: '5px',
  backgroundColor: '#d3d3d3',
  outline: 'none',
  transition: 'opacity .2s',
  '&::-webkit-slider-thumb': {
    '-webkit-appearance': 'none',
    appearance: 'none',
    width: '8px',
    height: '8px',
    background: gray15,
    borderRadius: '50%',
    border: 'none',
    alignSelf: 'center',
    transition: 'transform 100ms ease'
  },
  '&::-webkit-slider-thumb:hover': {
    transform: 'scale(1.3)',
    cursor: 'pointer'
  },
  '&::-webkit-slider-thumb:active': {
    transform: 'scale(1)'
  },
  variants: {
    hideThumb: {
      true: {
        '&::-webkit-slider-thumb': {
          width: '2px',
          height: '2px'
        },
        '&:hover::-webkit-slider-thumb': {
          transform: 'scale(4)',
          cursor: 'pointer'
        }
      }
    },
    tall: {
      true: {
        height: '3px'
      }
    }
  }
})

interface Props {
  setGain: (number) => void
  value: number
  width?: string
  onMouseEnter?: (event: React.MouseEvent) => void
  onMouseLeave?: (event: React.MouseEvent) => void
  hideThumb?: boolean
  tall?: boolean
}
const iconClass = css({ width: '17px', height: '17px' })

const VolumeIcon = styled(Icons.VolumeIcon, iconClass)
const VolumeMuteIcon = styled(Icons.VolumeMuteIcon, iconClass)
const Container = StyledContainer
const Slider = StyledSlider
const VolumeButton = withTooltip(styled(IconButton, { marginRight: '8px' }))

const VolumeSlider: React.FC<Props> = ({
  setGain,
  width,
  value,
  onMouseEnter,
  onMouseLeave,
  hideThumb,
  tall
}) => {
  const { studio } = useTreeState()
  const [savedVolume, setSavedVolume] = React.useState(null)
  const muteOrUnmute = () => {
    if (value === 0) {
      setGain(savedVolume || 0)
    } else {
      setSavedVolume(value)
      setGain(0)
    }
  }

  const base = 16
  const maxValue = 128
  const icon = value === 0 ? <VolumeMuteIcon /> : <VolumeIcon />
  const rangeValue = (maxValue * Math.log(value * (base - 1) + 1)) / Math.log(base)
  const rangePercentage = (rangeValue / 128) * 100

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      <VolumeButton secondary={value === 0} onClick={muteOrUnmute} tooltipText={'Mute/Unmute'}>
        {icon}
      </VolumeButton>
      <Slider
        css={{
          backgroundImage: `linear-gradient(90deg, black ${rangePercentage}%, transparent ${rangePercentage}%)`
        }}
        type="range"
        value={rangeValue}
        onChange={(e) => {
          setGain((Math.pow(base, parseInt(e.target.value) / maxValue) - 1) / (base - 1))
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        min="0"
        max={maxValue}
        style={{ width: width || '100px' }}
        hideThumb={hideThumb}
        tall={tall}
      />
    </Container>
  )
}

export default observer(VolumeSlider)
